/**
 * 补 TA 的身体特点（WIRE-BODY-01，总账 N-05 的 A1；I 10-09 照 6c2b 推荐定六条）：表、取细节、核对、拼说法——纯函数，页面在 BodyInfo.tsx。
 * 依据 BODY-0 §2.4、§2.5、§11（E:/petsoul-audit/petsoul/docs/coordination/BODY-0-DRAFT-P-2026-09-27.md）与契约 BodyProfileView、BodyStatementItem。
 * - 用词只说「TA 的身体特点」，不说残疾、缺陷、异常，不问原因（§11.1）。部位、组的中文名照 §11.6，左右按宠物自己的左右。
 * - 只问缺的那一组：形象卡在 body_info_needed 时，CharacterState.reason_details 给的 group 和 part_ids（I 定第 2 条）。
 *   先问数量（默认是档案里已记的数量，没记过就是体型满数），和满数不同时再逐个问部位：在、不在、只有一部分；只有一个部位的组（尾巴）直接问它。
 * - 体型、各组有哪几个部位照后端那一份（A 的 web_character/body.py GROUP_PARTS、P 的 body_input.py BODY_PLANS）抄成前端的表；
 *   体型本身一律读档案里的 body_plan，前端不按物种推。
 * - 核对规则照后端写入前的两条（body_writes.py 的 _append_reality_in、body.py 的 check_reality）：这一组「在」的不能多于说的数量，
 *   「不在」的不能多于满数减数量。前端先核一遍，只为当场说清哪里对不上；后端照样再核（422 body_value_invalid）。
 * - 部位图示（ASSET-UI-032，三种体型各一张，背面视角：画面左边就是 TA 自己的左边）：出不出现只看 show_body_diagram（后端算好，§11.1）。
 *   图本身 aria-hidden，叠在图上的点按区按下面的落点放（480×480 的坐标，取在各部位上，不按路径面积）；读屏走文字列表。
 */
import type { BodyDogSize, BodyGroup, BodyPartId, BodyPlan, BodyProfileView, BodyStatementItem, BodyStatementState, CharacterState } from "@/shared/contracts";

/** 部位中文名（§11.6，前端 Record 穷举：契约 BodyPartId 增减时编译不过） */
export const BODY_PART_NAME: Record<BodyPartId, string> = {
  head: "头",
  ear_l: "左耳",
  ear_r: "右耳",
  limb_fl: "左前腿",
  limb_fr: "右前腿",
  limb_hl: "左后腿",
  limb_hr: "右后腿",
  tail: "尾巴",
  torso: "身体",
  wing_l: "左翅膀",
  wing_r: "右翅膀",
  leg_l: "左腿",
  leg_r: "右腿",
};

/** 组的中文名（§11.6）与量词 */
export const BODY_GROUP_NAME: Record<BodyGroup, string> = { limbs: "腿", tails: "尾巴", ears: "耳朵", wings: "翅膀" };
const GROUP_UNIT: Record<BodyGroup, string> = { limbs: "条", tails: "条", ears: "只", wings: "只" };

const LEFT: ReadonlySet<string> = new Set(["ear_l", "limb_fl", "limb_hl", "wing_l", "leg_l"]);
const RIGHT: ReadonlySet<string> = new Set(["ear_r", "limb_fr", "limb_hr", "wing_r", "leg_r"]);

/** 文字列表里一处的说法：左右两边的部位后面写明是 TA 自己的哪一边（I 定第 3 条：首期文字列表写明「TA 自己的左边」） */
export function bodyPartLabel(part: BodyPartId): string {
  const name = BODY_PART_NAME[part];
  if (LEFT.has(part)) return `${name}（TA 自己的左边）`;
  if (RIGHT.has(part)) return `${name}（TA 自己的右边）`;
  return name;
}

/** 体型 → 部位（规范顺序，同后端 BODY_PLANS） */
export const BODY_PLAN_PARTS: Record<BodyPlan, readonly BodyPartId[]> = {
  quadruped_tailed: ["head", "ear_l", "ear_r", "limb_fl", "limb_fr", "limb_hl", "limb_hr", "tail", "torso"],
  quadruped_tailless: ["head", "ear_l", "ear_r", "limb_fl", "limb_fr", "limb_hl", "limb_hr", "torso"],
  bird: ["head", "wing_l", "wing_r", "leg_l", "leg_r", "tail", "torso"],
};

/** 体型 → 组 → 部位（同后端 GROUP_PARTS；鸟的两条腿也叫 limbs；不带尾巴的四足没有 tails 这一组） */
export const BODY_PLAN_GROUPS: Record<BodyPlan, Partial<Record<BodyGroup, readonly BodyPartId[]>>> = {
  quadruped_tailed: { limbs: ["limb_fl", "limb_fr", "limb_hl", "limb_hr"], tails: ["tail"], ears: ["ear_l", "ear_r"] },
  quadruped_tailless: { limbs: ["limb_fl", "limb_fr", "limb_hl", "limb_hr"], ears: ["ear_l", "ear_r"] },
  bird: { wings: ["wing_l", "wing_r"], limbs: ["leg_l", "leg_r"], tails: ["tail"] },
};

/** 部位图示（ASSET-UI-032 v1，原字节拷进 public，出处见同目录 SOURCE.md）：按档案的 body_plan 选，不按物种推 */
export const BODY_DIAGRAM_SRC: Record<BodyPlan, string> = {
  quadruped_tailed: "/ui-assets/ASSET-UI-032/v1/quadruped_tailed.svg",
  quadruped_tailless: "/ui-assets/ASSET-UI-032/v1/quadruped_tailless.svg",
  bird: "/ui-assets/ASSET-UI-032/v1/bird.svg",
};

/**
 * 图上点按区的落点（图的 480×480 坐标，取在各部位上；两只无尾四足同一套）。同一组里两处的中心至少隔 88（显示成 240 宽时 44px），
 * 点按区不叠在一起；靠边的也整个落在图里。头、身体只在后端报的组认不出时才会被问到，那时不放图上的点按区（见 bodySpotsFor）。
 */
const QUADRUPED_SPOTS: Partial<Record<BodyPartId, readonly [number, number]>> = {
  ear_l: [182, 80],
  ear_r: [298, 80],
  limb_fl: [164, 248],
  limb_fr: [316, 248],
  limb_hl: [182, 385],
  limb_hr: [298, 385],
  tail: [246, 425],
};
export const BODY_DIAGRAM_SPOTS: Record<BodyPlan, Partial<Record<BodyPartId, readonly [number, number]>>> = {
  quadruped_tailed: QUADRUPED_SPOTS,
  quadruped_tailless: { ...QUADRUPED_SPOTS, tail: undefined },
  bird: { wing_l: [128, 262], wing_r: [352, 262], leg_l: [184, 402], leg_r: [296, 402], tail: [240, 420] },
};

/** 图上要放点按区的那几处（这次问到的、图上有落点的） */
export function bodySpotsFor(plan: BodyPlan, parts: readonly BodyPartId[]): { part: BodyPartId; x: number; y: number }[] {
  const spots = BODY_DIAGRAM_SPOTS[plan];
  return parts.flatMap((part) => {
    const at = spots[part];
    return at ? [{ part, x: at[0], y: at[1] }] : [];
  });
}

const PART_IDS: ReadonlySet<string> = new Set(Object.keys(BODY_PART_NAME));
const isPartId = (value: unknown): value is BodyPartId => typeof value === "string" && PART_IDS.has(value);

/**
 * body_info_needed 的细节（CharacterState.reason_details：读时按此刻的档案现算）。认不出的部位编号丢掉；一个都不剩、不是对象都算没有。
 * 只看细节本身，原因码由调用方核（形象页按 characterReasonCode，到家等待页按没画成那一条的原因）。
 */
export function bodyDetailsOf(state: CharacterState | null | undefined): { group: string; partIds: BodyPartId[] } | null {
  const raw = state?.reason_details;
  if (!raw || typeof raw !== "object" || typeof raw.group !== "string" || !Array.isArray(raw.part_ids)) return null;
  const partIds = raw.part_ids.filter(isPartId);
  return partIds.length ? { group: raw.group, partIds } : null;
}

/** 这一次要问什么：哪一组（认不出的组为 null）、这一组有哪几处（members）、问不问数量（这一组至少两处才问）、逐个问哪几处（parts，规范顺序） */
export type BodyAsk = { group: BodyGroup | null; members: readonly BodyPartId[]; countable: boolean; parts: BodyPartId[] };

/** 常驻入口里能记的几组（按档案的体型；没有体型就没有入口） */
export function bodyRecordGroups(plan: BodyPlan | null | undefined): BodyGroup[] {
  return plan ? (Object.keys(BODY_PLAN_GROUPS[plan]) as BodyGroup[]) : [];
}

/** 常驻入口选了一组：整组都问（和卡着时只问缺的那几处不同） */
export function bodyRecordAskOf(plan: BodyPlan, group: BodyGroup): BodyAsk | null {
  const members = BODY_PLAN_GROUPS[plan][group];
  return members ? bodyAskOf(plan, { group, partIds: members }) : null;
}

export function bodyAskOf(plan: BodyPlan, details: { group: string; partIds: readonly BodyPartId[] }): BodyAsk | null {
  const groups = BODY_PLAN_GROUPS[plan];
  const known = Object.prototype.hasOwnProperty.call(groups, details.group) ? (details.group as BodyGroup) : null;
  const members = known ? groups[known]! : [];
  const parts = BODY_PLAN_PARTS[plan].filter((part) => details.partIds.includes(part) && (!known || members.includes(part)));
  if (!parts.length) return null;
  return { group: known, members, countable: members.length >= 2, parts };
}

/** 问数量时默认选哪个：档案里这一组已经记过数量就用它，没记过就是满数（物种基线） */
export function bodyDefaultCount(body: BodyProfileView, ask: BodyAsk): number {
  return body.groups.find((g) => g.group === ask.group)?.count ?? ask.members.length;
}

/** 要不要逐个问部位：不问数量的组（尾巴、认不出的组）直接问；问数量的组只在说的数量比满数少时问 */
export function bodyPartsShown(ask: BodyAsk, count: number): boolean {
  return !ask.countable || count < ask.members.length;
}

export type BodyForm = { count: number; states: Partial<Record<BodyPartId, BodyStatementState>>; dog: BodyDogSize | null };

/**
 * 记下之前先核：ok／incomplete（逐个问的还有没选的）／mismatch（和说的数量对不上）。
 * 这一组里没问到的部位，按档案里已确认的状态算（没确认的不算）；规则同后端：「在」≤ 数量，「不在」≤ 满数 − 数量。
 */
export function bodyFormCheck(body: BodyProfileView, ask: BodyAsk, form: BodyForm): "ok" | "incomplete" | "mismatch" {
  if (ask.countable && (!Number.isInteger(form.count) || form.count < 0 || form.count > ask.members.length)) return "mismatch";
  const shown = bodyPartsShown(ask, form.count);
  if (shown && ask.parts.some((part) => !form.states[part])) return "incomplete";
  if (!ask.countable) return "ok";
  const total = ask.members.length;
  const stateOf = (part: BodyPartId): string | null => {
    if (ask.parts.includes(part)) return shown ? form.states[part] ?? null : "present";
    const known = body.parts[part];
    return known && known.confirmation === "confirmed" ? known.state : null;
  };
  const states = ask.members.map(stateOf);
  const present = states.filter((s) => s === "present").length;
  const absent = states.filter((s) => s === "absent").length;
  return present > form.count || absent > total - form.count ? "mismatch" : "ok";
}

/** 狗的大小顺带问一次：只对狗、档案说还没确认（species_default_unconfirmed）时问（§2.5；I 定第 7 条：只在面板打开时顺带问） */
export function bodyAsksDogSize(body: BodyProfileView): boolean {
  return body.species === "dog" && body.size_class_basis === "species_default_unconfirmed";
}

/** 拼成要提交的说法（BodyStatementItem：每种只给自己的字段，其余 null） */
export function bodyItemsOf(ask: BodyAsk, form: BodyForm, dogAsked: boolean): BodyStatementItem[] {
  const items: BodyStatementItem[] = [];
  if (ask.countable && ask.group) items.push({ kind: "group_count", group: ask.group, count: form.count, part_id: null, state: null, value: null });
  if (bodyPartsShown(ask, form.count)) {
    for (const part of ask.parts) {
      const state = form.states[part];
      if (state) items.push({ kind: "part_state", group: null, count: null, part_id: part, state, value: null });
    }
  }
  if (dogAsked && form.dog) items.push({ kind: "dog_size", group: null, count: null, part_id: null, state: null, value: form.dog });
  return items;
}

/** 部位状态的说法（文字列表里三个选项、待确认那一条的摘要用同一套） */
export const BODY_STATE_NAME: Record<BodyStatementState, string> = { present: "在", absent: "不在", partial: "只有一部分" };
/** 狗的大小两个选项（§2.5 原话） */
export const BODY_DOG_SIZE_NAME: Record<BodyDogSize, string> = { small: "小狗（大约抱得起来）", large: "大狗" };

/** 一条说法的摘要（待确认那一条给管理员看）：「腿：3 条；左后腿：不在」。认不出的条目跳过，不露编号 */
export function bodyStatementText(items: readonly BodyStatementItem[]): string {
  const said = items.flatMap((item) => {
    if (item.kind === "group_count" && item.group && item.count !== null && Object.prototype.hasOwnProperty.call(BODY_GROUP_NAME, item.group)) {
      return [`${BODY_GROUP_NAME[item.group]}：${item.count} ${GROUP_UNIT[item.group]}`];
    }
    if (item.kind === "part_state" && isPartId(item.part_id) && item.state && Object.prototype.hasOwnProperty.call(BODY_STATE_NAME, item.state)) {
      return [`${BODY_PART_NAME[item.part_id]}：${BODY_STATE_NAME[item.state]}`];
    }
    if (item.kind === "dog_size" && item.value && Object.prototype.hasOwnProperty.call(BODY_DOG_SIZE_NAME, item.value)) {
      return [`大小：${BODY_DOG_SIZE_NAME[item.value]}`];
    }
    return [];
  });
  return said.join("；");
}

/** 问数量那一句：「TA 有几条腿？」；选项「4 条」 */
export function bodyCountQuestion(group: BodyGroup): string {
  return `TA 有几${GROUP_UNIT[group]}${BODY_GROUP_NAME[group]}？`;
}
export function bodyCountOption(group: BodyGroup, count: number): string {
  return `${count} ${GROUP_UNIT[group]}`;
}
/** 逐个问部位那一句：问数量的组说「具体是哪几条？」；单问一处的（尾巴、认不出的组）说「TA 的尾巴现在是什么样？」 */
export function bodyPartsQuestion(ask: BodyAsk): string {
  if (ask.countable && ask.group) return `具体是哪几${GROUP_UNIT[ask.group]}？`;
  if (ask.parts.length === 1) return `TA 的${BODY_PART_NAME[ask.parts[0]]}现在是什么样？`;
  return "这几处现在是什么样？";
}

/* ---------------- 页面上的话（新写的每一句都在这里，README 文案表逐句列） ---------------- */

export const BODY_CARD_TITLE = "TA 的身体特点";
/** 形象页那张卡（I 定第 1 条原话：「补一下 TA 的腿的情况，就能画全身」）；组认不出时只说身体特点 */
export function bodyAskLine(group: string): string {
  return Object.prototype.hasOwnProperty.call(BODY_GROUP_NAME, group)
    ? `补一下 TA 的${BODY_GROUP_NAME[group as BodyGroup]}的情况，就能画全身。`
    : "补一下 TA 的身体特点，就能画全身。";
}
export const BODY_OPEN_ASK = "去补一下";
/** 常驻入口（I 10-15）：形象没卡着时，管理员在形象页上看到的只有这一行 */
export const BODY_ENTRY = "记一下 TA 的身体特点";
export const BODY_PICK_GROUP = "想记哪一处？";
export const BODY_RECORD_NOTE = "照 TA 现在的样子记，以后画 TA 都照这些来。";
export const BODY_OPEN_REVIEW = "去看看";
/** 待确认（I 定第 5 条原话：「有 N 条待你确认」） */
export function bodyToConfirmLine(count: number): string {
  return `有 ${count} 条待你确认。`;
}
/** 照片与档案冲突（§11.3 原话，补了问号）：不说照片里看到了什么 */
export const BODY_CONFLICT_LINE = "有一张新照片和档案里记的不一样，要不要看看？";
export const BODY_SIDE_NOTE = "按 TA 自己的左右选。";
export const BODY_DIAGRAM_LEFT = "TA 的左边";
export const BODY_DIAGRAM_RIGHT = "TA 的右边";
export const BODY_DIAGRAM_CAPTION = "从背后看 TA";
export const BODY_DOG_QUESTION = "顺带问一下：TA 是小狗还是大狗？";
export const BODY_SUBMIT = "记下";
export const BODY_MISMATCH = "这几处和上面说的数量对不上，再看看。";
/**
 * 管理员记下、当场生效以后说的那一句，两版（I 10-09 21:23 定甲）：
 * - 后端 A-BODY-RESUME-01 进组合、能力表报 character.body_resume 可用以后：「记下了，TA 的全身会接着画。」（补完生效后同一次自动接着画、不另算一次）；
 * - 之前（能力表里没有这一项，或不是可用）：不许诺自动——以后再画全身照这些来，要不要现在重新准备由主人在上面点。
 * 补完这一组还有别的部位不知道（世界里还有 unknown）：两版都不说画，只说还有一处要补。
 */
export const BODY_SAVED_RESUME = "记下了，TA 的全身会接着画。";
export const BODY_SAVED = "记下了，以后画 TA 的全身都照这些来。";
export const BODY_SAVED_MORE = "记下了，还有一处要补。";
/** 成员提交进待确认（I 定第 5 条：照旧写「已交给家里的管理员确认」） */
export const BODY_SENT_TO_ADMIN = "已交给家里的管理员确认。";
export const BODY_CONFIRMED = "记下了。";
export const BODY_REJECTED = "这一条没有记。";
export const BODY_KEPT = "照档案里记的，没有改。";
export const BODY_PENDING_TITLE = "家里人补的，等你确认";
export const BODY_CONFIRM = "确认";
export const BODY_REJECT = "先不记";
export const BODY_KEEP = "按档案";
export const BODY_CORRECT = "按照片改";
export function bodyCorrectQuestion(part: BodyPartId): string {
  return `${bodyPartLabel(part)}现在是什么样？`;
}
/** 写命令出错时说的话（不露原因码）：版本变了、已经处理过、对不上、没权限、结果不明（同一把键再点不会重复记） */
export const BODY_ERROR_CHANGED = "家里人刚改过 TA 的身体特点，先看看现在的样子。";
export const BODY_ERROR_DONE = "这一条已经处理过了。";
export const BODY_ERROR_INVALID = "这样记下来前后对不上，再看看。";
export const BODY_ERROR_FORBIDDEN = "你现在还不能改这一项。";
export const BODY_ERROR_UNSURE = "还没确认记下了没有，再点一次不会重复记。";
export const BODY_READ_FAILED = "TA 的身体特点暂时读不到。";

export const BODY_RETRY = "再试一次";

/** 身体档案这一条只读查询的键（形象页的卡、到家等待页共用一条缓存） */
export function bodyQueryKey(userId: string | null, petId: string) {
  return ["pets", "body", userId, petId] as const;
}

/** 世界里此刻还有没有不知道的部位（写命令回来的档案；空表按「还不知道」算） */
export function bodyStillUnknown(body: BodyProfileView): boolean {
  const states = Object.values(body.world_parts);
  return states.length === 0 || states.some((state) => state === "unknown");
}
